import React, {useEffect, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../../core/constants/enums";
import {PrintCatalogReviewDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../../components/base/chips/chips";
import {ReactComponent as NoColorIcon} from "../../../../../../../../assets/images/notifications/no-color.svg";
import TagChipsContent from "../../../../../../../components/app-specific/chips-contents/tag-chips-content";
import {Col, Row} from "reactstrap";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";


const dataGridSaveConfig = {
    name: "PrintCatalogueViewReviewSectionDataGrid",
    version: 1,
}

const _initialPaginationInfo = {
    ...DefaultPaginationInfo,
}

const PrintCatalogueViewReviewSection = ({
                                             reviewState,
                                             updatingStep,
                                         }) => {
    const [visibleProducts, setVisibleProducts] = useState([]);

    const columns = useMemo(() =>
            /**@type {DataGridColumn[]}*/
            [
                {
                    name: PrintCatalogReviewDataGridColumnNames.categoryColor,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 0,
                    resizable: false,
                },
                {
                    title: 'Category',
                    name: PrintCatalogReviewDataGridColumnNames.category,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                },
                {
                    title: 'Make',
                    name: PrintCatalogReviewDataGridColumnNames.make,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                },
                {
                    title: 'Part No',
                    name: PrintCatalogReviewDataGridColumnNames.partNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                },
                {
                    title: 'Tags',
                    name: PrintCatalogReviewDataGridColumnNames.tags,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                },
            ]
        , [])

    const [dataGridColumns, _, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);


    /**
     * With each change in the [credits] and [paginationInfo] values of the state:
     * - selects a slice of the credits to be set as the visible credits.
     */
    useEffect(() => {
        if (!reviewState.products?.length) {
            setPaginationInfo(initialPaginationInfo);
            return setVisibleProducts([]);
        }
        const start = Math.min(Math.max((paginationInfo.currentPage - 1) * paginationInfo.pageSize, 0), reviewState.products.length);
        const end = Math.min((paginationInfo.currentPage) * paginationInfo.pageSize, reviewState.products.length);
        const chunk = Array.from(reviewState.products).slice(start, end)
        setPaginationInfo(prevState => ({...prevState, length: reviewState.products.length}));
        setVisibleProducts(chunk);
    }, [initialPaginationInfo, paginationInfo.currentPage, paginationInfo.pageSize, reviewState.products])


    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return visibleProducts
                ?.map((product) => {
                    return {
                        key: product.id,
                        data: product,
                        cells: {
                            [PrintCatalogReviewDataGridColumnNames.categoryColor]:
                                <div {...dataGridCellTextProps} className={'color-circle'}>
                                    <NoColorIcon className={'placeholder'}/>
                                    <div className={'circle'} style={{'--color': product?.category?.colorCode}}/>
                                </div>,
                            [PrintCatalogReviewDataGridColumnNames.category]: product?.category?.title?.concat((product?.category?.industryTitle ?? product?.category?.industry?.title) ? ` (${product?.category?.industryTitle ?? product?.category?.industry?.title})` : ''),
                            [PrintCatalogReviewDataGridColumnNames.make]: product?.make?.title,
                            [PrintCatalogReviewDataGridColumnNames.partNo]:
                                <ProductInformationPopover partNo={product?.partNo}>
                                    {product?.partNo ?? ''}
                                </ProductInformationPopover>,
                            [PrintCatalogReviewDataGridColumnNames.tags]:
                                <div className={'d-flex flex-wrap'}>
                                    {
                                        product?.tags?.map(tag => (
                                            <span key={tag.id} {...dataGridCellTextProps} className={'mr-2 mb-2'}>
                                                <Chips
                                                    contentType={ChipsContentType.element}
                                                    type={ChipsTypes.none}
                                                    iconPosition={ChipsIconPosition.start}
                                                    onIconClick={() => void 0}
                                                    content={<TagChipsContent tag={tag} useIcon/>}
                                                />
                                            </span>

                                        ))
                                    }
                                </div>,
                        }
                    }
                })
            ?? []
    }, [visibleProducts])

    return (
        <>
            <Row className={'w-100'}>
                <Col xs={12}>
                    <p className={'text-md font-weight-semi-bold'}>
                        Review Included Products
                    </p>
                </Col>
                <Col xs={12}>
                    <PartnerDataGrid
                        hideFooterActions
                        hideExporting
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        rows={dataGridRows}
                        columns={dataGridColumns}
                        state={{
                            loading: {state: updatingStep},
                            pagination: paginationInfo,
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                        onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                    />
                </Col>
            </Row>
        </>
    );
}

export default PrintCatalogueViewReviewSection;
